import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { getApiBase, getAuthHeaders } from '../../utils/api';

// Duffel CDN logo helper
const getDuffelLogoUrl = (code) => code ? `https://assets.duffel.com/img/airlines/for-light-background/full-color-logo/${code}.svg` : null;

const AirlineLogo = ({ code, size = 'sm' }) => {
  const logo = getDuffelLogoUrl(code);
  const sizeClass = size === 'lg' ? 'h-8 w-8' : size === 'md' ? 'h-6 w-6' : 'h-5 w-5';
  return logo ? (
    <img src={logo} alt={code} className={`${sizeClass} object-contain`}
      onError={(e) => { e.target.style.display = 'none'; }} />
  ) : (
    <span className={`${sizeClass} bg-gray-200 rounded-full flex items-center justify-center text-gray-600 font-bold text-[10px]`}>{code}</span>
  );
};

// ---- Status Badge ----
const statusColors = {
  confirmed: { badge: 'bg-green-100 text-green-700', border: 'border-l-green-500' },
  cancelled: { badge: 'bg-red-100 text-red-700', border: 'border-l-red-400' },
  changed: { badge: 'bg-blue-100 text-blue-700', border: 'border-l-blue-500' },
  airline_change_pending: { badge: 'bg-orange-100 text-orange-700', border: 'border-l-orange-500' },
};

const StatusBadge = ({ status }) => {
  const style = statusColors[status]?.badge || 'bg-gray-100 text-gray-600';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${style}`}>
      {status === 'airline_change_pending' ? 'Airline Change' : (status || 'unknown').charAt(0).toUpperCase() + (status || 'unknown').slice(1)}
    </span>
  );
};

// ---- Format helpers ----
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
const fmtDateShort = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';
const fmtTime = (d) => d ? new Date(d).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true }) : '';
const fmtCurrency = (amount, currency) => {
  if (!amount) return '';
  const sym = currency === 'USD' ? '$' : currency === 'EUR' ? '\u20AC' : currency === 'GBP' ? '\u00A3' : (currency || '') + ' ';
  return `${sym}${parseFloat(amount).toFixed(2)}`;
};

// ---- Shared: Night count helper ----
const calcNights = (checkIn, checkOut) => {
  if (!checkIn || !checkOut) return 0;
  return Math.max(1, Math.ceil((new Date(checkOut) - new Date(checkIn)) / 86400000));
};

// ---- Shared: Group bookings into Upcoming / Past ----
const groupByTime = (bookings, dateField) => {
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const upcoming = [];
  const past = [];
  bookings.forEach(b => {
    const d = b[dateField] ? new Date(b[dateField]) : null;
    if (d && d >= now) upcoming.push(b);
    else past.push(b);
  });
  return { upcoming, past };
};

// ---- Shared: Section Divider ----
const SectionDivider = ({ label, count }) => (
  <div className="sticky top-0 z-10 bg-white/95 backdrop-blur-sm py-2 mb-2 mt-4 first:mt-0">
    <div className="flex items-center space-x-2">
      <span className="text-sm font-semibold text-gray-600 uppercase tracking-wider">{label}</span>
      {count > 0 && (
        <span className="bg-gray-200 text-gray-600 text-xs font-medium px-1.5 py-0.5 rounded-full">{count}</span>
      )}
      <div className="flex-1 border-t border-gray-200"></div>
    </div>
  </div>
);

// ---- Shared: Empty State ----
const EmptyState = ({ icon, title, subtitle }) => (
  <div className="text-center py-16 bg-gray-50 rounded-xl">
    <div className="text-5xl mb-3 opacity-80">{icon}</div>
    <p className="text-gray-600 font-medium text-base">{title}</p>
    <p className="text-sm text-gray-400 mt-1">{subtitle}</p>
  </div>
);

// ---- Shared: Responsive Modal ----
// Mobile (<640px): bottom sheet; Desktop: centered modal
const ResponsiveModal = ({ onClose, maxWidth = 'max-w-3xl', zIndex = 'z-[70]', children }) => (
  <div className={`fixed inset-0 bg-black/40 backdrop-blur-[2px] ${zIndex}`} onClick={onClose}>
    {/* Desktop centered */}
    <div className="hidden sm:flex items-start justify-center min-h-screen pt-12 pb-8 px-4">
      <div className={`${maxWidth} w-full bg-white rounded-xl shadow-2xl overflow-hidden`}
        onClick={e => e.stopPropagation()}>
        {children}
      </div>
    </div>
    {/* Mobile bottom sheet */}
    <div className="sm:hidden fixed inset-x-0 bottom-0 max-h-[92vh] overflow-hidden" onClick={e => e.stopPropagation()}>
      <div className="bg-white rounded-t-2xl shadow-2xl max-h-[92vh] flex flex-col">
        <div className="flex justify-center pt-3 pb-1">
          <div className="w-10 h-1 bg-gray-300 rounded-full"></div>
        </div>
        {children}
      </div>
    </div>
  </div>
);

// ---- Shared: BookingCard wrapper ----
const BookingCard = ({ status, onClick, children }) => {
  const borderColor = statusColors[status]?.border || 'border-l-gray-300';
  return (
    <div
      onClick={onClick}
      className={`border border-gray-200 rounded-lg border-l-4 ${borderColor} p-3 sm:p-4 group hover:shadow-md hover:border-gray-300 transition-all cursor-pointer`}
    >
      {children}
    </div>
  );
};

// ---- Cancel Flow Modal ----
const CancelFlowModal = ({ booking, onClose, onCancelled }) => {
  const [step, setStep] = useState('loading'); // loading, quote, confirming, done, error
  const [quote, setQuote] = useState(null);
  const [error, setError] = useState('');
  const apiBase = getApiBase();

  useEffect(() => {
    const fetchQuote = async () => {
      try {
        const res = await axios.post(`${apiBase}/bookings/flights/${booking.id}/cancel/quote`, {}, { headers: getAuthHeaders() });
        if (res.data.success) {
          setQuote(res.data.data);
          setStep('quote');
        } else {
          setError(res.data.error || 'Could not get cancellation quote');
          setStep('error');
        }
      } catch (e) {
        setError(e.response?.data?.error || e.message);
        setStep('error');
      }
    };
    fetchQuote();
  }, [booking.id, apiBase]);

  const handleConfirm = async () => {
    setStep('confirming');
    try {
      const res = await axios.post(`${apiBase}/bookings/flights/${booking.id}/cancel/confirm`, {
        cancellation_id: quote.id,
      }, { headers: getAuthHeaders() });
      if (res.data.success) {
        setStep('done');
        onCancelled();
      } else {
        setError(res.data.error || 'Cancellation failed');
        setStep('error');
      }
    } catch (e) {
      setError(e.response?.data?.error || e.message);
      setStep('error');
    }
  };

  return (
    <ResponsiveModal onClose={onClose} maxWidth="max-w-md" zIndex="z-[80]">
      <div className="p-5 sm:p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Cancel Booking</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl font-bold">&times;</button>
        </div>

        {step === 'loading' && (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-3 text-gray-600">Getting cancellation quote...</p>
          </div>
        )}

        {step === 'quote' && quote && (
          <div>
            {quote.non_refundable ? (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-red-800 font-medium">Non-Refundable Fare</p>
                <p className="mt-1 text-sm text-red-700">This booking is non-refundable. No refund will be issued if you cancel.</p>
              </div>
            ) : (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-yellow-800 font-medium">Cancellation Quote</p>
                <div className="mt-2 space-y-1 text-sm text-yellow-700">
                  {quote.penalty_amount && parseFloat(quote.penalty_amount) > 0 && (
                    <div className="flex justify-between">
                      <span>Cancellation fee:</span>
                      <span className="font-medium text-red-600">{fmtCurrency(quote.penalty_amount, quote.penalty_currency)}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span>Refund amount:</span>
                    <span className="font-bold">{quote.refund_amount && parseFloat(quote.refund_amount) > 0 ? fmtCurrency(quote.refund_amount, quote.refund_currency) : 'No refund'}</span>
                  </div>
                  {quote.refund_to && (
                    <div className="flex justify-between">
                      <span>Refunded to:</span>
                      <span>{quote.refund_to === 'arc_bsp_cash' ? 'Original payment' : quote.refund_to === 'airline_credits' ? 'Airline credits' : quote.refund_to}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
            <p className="text-sm text-gray-600 mb-4">This action cannot be undone. Are you sure you want to cancel this booking?</p>
            <div className="flex space-x-3">
              <button onClick={onClose} className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50">
                Keep Booking
              </button>
              <button onClick={handleConfirm} className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md text-sm font-medium">
                Confirm Cancellation
              </button>
            </div>
          </div>
        )}

        {step === 'confirming' && (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600 mx-auto"></div>
            <p className="mt-3 text-gray-600">Cancelling booking...</p>
          </div>
        )}

        {step === 'done' && (
          <div className="text-center py-6">
            <div className="text-green-600 text-4xl mb-3">&#10003;</div>
            <p className="text-lg font-medium">Booking Cancelled</p>
            {quote?.refund_amount && parseFloat(quote.refund_amount) > 0 && (
              <p className="text-sm text-gray-600 mt-1">
                A refund of {fmtCurrency(quote.refund_amount, quote.refund_currency)} will be processed.
              </p>
            )}
            <button onClick={onClose} className="mt-4 px-6 py-2 bg-gray-200 hover:bg-gray-300 rounded-md text-sm font-medium">
              Close
            </button>
          </div>
        )}

        {step === 'error' && (
          <div className="text-center py-6">
            <div className="text-red-500 text-4xl mb-3">&#10007;</div>
            <p className="text-lg font-medium text-red-700">Error</p>
            <p className="text-sm text-gray-600 mt-1">{error}</p>
            <button onClick={onClose} className="mt-4 px-6 py-2 bg-gray-200 hover:bg-gray-300 rounded-md text-sm font-medium">
              Close
            </button>
          </div>
        )}
      </div>
    </ResponsiveModal>
  );
};

// ---- Change Flow Modal ----
const ChangeFlowModal = ({ booking, onClose, onChanged }) => {
  const [step, setStep] = useState('select'); // select, searching, offers, detail, confirming, done, error
  const [selectedSlices, setSelectedSlices] = useState([]);
  const [newDates, setNewDates] = useState({});
  const [changeOffers, setChangeOffers] = useState([]);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [detailOffer, setDetailOffer] = useState(null);
  const [error, setError] = useState('');
  const apiBase = getApiBase();
  

  const slices = booking.slices || [];

  const toggleSlice = (sliceId) => {
    setSelectedSlices(prev =>
      prev.includes(sliceId) ? prev.filter(s => s !== sliceId) : [...prev, sliceId]
    );
  };

  const handleSearch = async () => {
    if (selectedSlices.length === 0) {
      setError('Select at least one flight segment to change');
      return;
    }

    setStep('searching');
    setError('');

    try {
      const slicesToRemove = selectedSlices.map(id => ({ slice_id: id }));
      const slicesToAdd = selectedSlices.map(id => {
        const slice = slices.find(s => s.id === id);
        return {
          origin: slice?.origin || '',
          destination: slice?.destination || '',
          departure_date: newDates[id] || slice?.departure_date || '',
          cabin_class: booking.class_type?.toLowerCase() || 'economy',
        };
      });

      const res = await axios.post(
        `${apiBase}/bookings/flights/${booking.id}/change/search`,
        { slices_to_remove: slicesToRemove, slices_to_add: slicesToAdd },
        { headers: getAuthHeaders() }
      );

      if (res.data.success) {
        setChangeOffers(res.data.data?.offers || []);
        setStep('offers');
      } else {
        setError(res.data.error || 'No change options found');
        setStep('error');
      }
    } catch (e) {
      setError(e.response?.data?.error || e.message);
      setStep('error');
    }
  };

  const handleConfirmChange = async (offerOverride) => {
    const offer = offerOverride || selectedOffer;
    if (!offer) return;
    setSelectedOffer(offer);
    setStep('confirming');

    try {
      const payload = { change_offer_id: offer.id };
      // Include payment if change costs money
      if (offer.change_total_amount && parseFloat(offer.change_total_amount) > 0) {
        payload.payment = { type: 'balance', amount: offer.change_total_amount, currency: offer.change_total_currency };
      }

      const res = await axios.post(
        `${apiBase}/bookings/flights/${booking.id}/change/confirm`,
        payload,
        { headers: getAuthHeaders() }
      );

      if (res.data.success) {
        setStep('done');
        onChanged();
      } else {
        setError(res.data.error || 'Change failed');
        setStep('error');
      }
    } catch (e) {
      setError(e.response?.data?.error || e.message);
      setStep('error');
    }
  };

  return (
    <ResponsiveModal onClose={onClose} maxWidth="max-w-2xl" zIndex="z-[80]">
      <div className="p-5 sm:p-6 overflow-y-auto max-h-[85vh] sm:max-h-[80vh]">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Change Flight</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl font-bold">&times;</button>
        </div>

        {/* Step 1: Select slices to change */}
        {step === 'select' && (
          <div>
            <p className="text-sm text-gray-600 mb-4">Select which flight(s) you'd like to change and pick new dates.</p>

            {slices.length === 0 ? (
              <p className="text-sm text-red-600">Unable to load flight segments. Try viewing booking details first.</p>
            ) : (
              <div className="space-y-3 mb-4">
                {slices.map((slice, idx) => (
                  <div key={slice.id} className={`border rounded-lg p-4 cursor-pointer transition-colors ${selectedSlices.includes(slice.id) ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}
                    onClick={() => toggleSlice(slice.id)}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <input type="checkbox" checked={selectedSlices.includes(slice.id)} readOnly className="h-4 w-4 text-blue-600" />
                        <div>
                          <span className="text-xs font-bold uppercase text-gray-500">{idx === 0 ? 'Outbound' : 'Return'}</span>
                          <div className="font-medium">{slice.origin} &rarr; {slice.destination}</div>
                          <div className="text-sm text-gray-500">{fmtDate(slice.departure_date)}</div>
                        </div>
                      </div>
                    </div>
                    {selectedSlices.includes(slice.id) && (
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <label className="text-xs text-gray-600 font-medium">New departure date</label>
                        <input
                          type="date"
                          value={newDates[slice.id] || slice.departure_date || ''}
                          onChange={(e) => { e.stopPropagation(); setNewDates(prev => ({...prev, [slice.id]: e.target.value})); }}
                          onClick={(e) => e.stopPropagation()}
                          className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {error && <p className="text-sm text-red-600 mb-3">{error}</p>}

            <div className="flex space-x-3">
              <button onClick={onClose} className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50">
                Cancel
              </button>
              <button onClick={handleSearch} disabled={selectedSlices.length === 0}
                className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white rounded-md text-sm font-medium">
                Search Alternatives
              </button>
            </div>
          </div>
        )}

        {/* Searching */}
        {step === 'searching' && (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-3 text-gray-600">Searching for alternatives...</p>
          </div>
        )}

        {/* Step 2: Show change offers */}
        {step === 'offers' && (
          <div>
            <p className="text-sm text-gray-600 mb-4">
              {changeOffers.length > 0 ? `${changeOffers.length} alternative${changeOffers.length > 1 ? 's' : ''} found` : 'No alternatives available'}
            </p>

            {changeOffers.length === 0 ? (
              <div className="text-center py-6">
                <p className="text-gray-500">No change options available for these dates.</p>
                <button onClick={() => setStep('select')} className="mt-3 text-blue-600 hover:text-blue-800 text-sm font-medium">
                  Try different dates
                </button>
              </div>
            ) : (
              <div className="space-y-3 mb-4 max-h-96 overflow-y-auto">
                {changeOffers.map((offer) => {
                  const addSlices = offer.slices?.add || [];
                  const changeAmt = parseFloat(offer.change_total_amount || 0);
                  const isSelected = selectedOffer?.id === offer.id;

                  return (
                    <div key={offer.id}
                      onClick={() => setSelectedOffer(offer)}
                      className={`border rounded-lg p-4 cursor-pointer transition-colors ${isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}>
                      {/* New itinerary segments */}
                      {addSlices.map((slice, sIdx) => (
                        <div key={sIdx} className="mb-2">
                          <div className="flex items-center space-x-2 text-sm">
                            <span className="font-medium">{slice.origin} &rarr; {slice.destination}</span>
                            <span className="text-gray-400">|</span>
                            <span className="text-gray-500">{fmtDate(slice.departure_date)}</span>
                            {slice.segments?.[0]?.cabinClassMarketingName && (
                              <span className="text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">{slice.segments[0].cabinClassMarketingName}</span>
                            )}
                          </div>
                          {slice.segments?.map((seg, segIdx) => (
                            <div key={segIdx} className="flex items-center space-x-2 text-xs text-gray-500 mt-1">
                              <AirlineLogo code={seg.carrierCode} size="sm" />
                              <span>{seg.carrierCode}{seg.number}</span>
                              <span>{fmtTime(seg.departure?.at)} &rarr; {fmtTime(seg.arrival?.at)}</span>
                              {seg.duration && <span className="text-gray-400">({seg.duration})</span>}
                            </div>
                          ))}
                        </div>
                      ))}

                      {/* Price difference + View Details */}
                      <div className="flex justify-between items-center mt-2 pt-2 border-t border-gray-100">
                        <button
                          onClick={(e) => { e.stopPropagation(); setDetailOffer(offer); setStep('detail'); }}
                          className="text-xs text-blue-600 hover:text-blue-800 font-medium underline">
                          View Details
                        </button>
                        <span className={`font-bold text-sm ${changeAmt > 0 ? 'text-red-600' : changeAmt < 0 ? 'text-green-600' : 'text-gray-600'}`}>
                          {changeAmt > 0 ? '+' : ''}{fmtCurrency(offer.change_total_amount, offer.change_total_currency)}
                          {changeAmt === 0 && ' (no extra cost)'}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {changeOffers.length > 0 && (
              <div className="flex space-x-3">
                <button onClick={() => setStep('select')} className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50">
                  Back
                </button>
                <button onClick={() => handleConfirmChange()} disabled={!selectedOffer}
                  className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white rounded-md text-sm font-medium">
                  Confirm Change
                </button>
              </div>
            )}
          </div>
        )}

        {/* Step: Offer Detail View */}
        {step === 'detail' && detailOffer && (
          <div>
            <button onClick={() => setStep('offers')} className="text-sm text-blue-600 hover:text-blue-800 font-medium mb-4 inline-flex items-center">
              &larr; Back to offers
            </button>

            {/* Pricing summary */}
            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <h4 className="font-medium text-sm mb-2">Pricing</h4>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="text-gray-600">Price difference</div>
                <div className={`font-bold text-right ${parseFloat(detailOffer.change_total_amount || 0) > 0 ? 'text-red-600' : parseFloat(detailOffer.change_total_amount || 0) < 0 ? 'text-green-600' : 'text-gray-700'}`}>
                  {parseFloat(detailOffer.change_total_amount || 0) > 0 ? '+' : ''}{fmtCurrency(detailOffer.change_total_amount, detailOffer.change_total_currency)}
                </div>
                {detailOffer.penalty_total_amount && (
                  <>
                    <div className="text-gray-600">Change penalty</div>
                    <div className="text-right text-orange-600 font-medium">{fmtCurrency(detailOffer.penalty_total_amount, detailOffer.penalty_total_currency)}</div>
                  </>
                )}
                {detailOffer.new_total_amount && (
                  <>
                    <div className="text-gray-600">New total</div>
                    <div className="text-right font-bold">{fmtCurrency(detailOffer.new_total_amount, detailOffer.new_total_currency)}</div>
                  </>
                )}
                {detailOffer.refund_to && (
                  <>
                    <div className="text-gray-600">Refund to</div>
                    <div className="text-right capitalize">{detailOffer.refund_to.replace(/_/g, ' ')}</div>
                  </>
                )}
              </div>
            </div>

            {/* Conditions */}
            {detailOffer.conditions && (
              <div className="mb-4">
                <h4 className="font-medium text-sm mb-2">Fare Conditions</h4>
                <div className="flex flex-wrap gap-2">
                  {detailOffer.conditions.change_before_departure && (
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${detailOffer.conditions.change_before_departure.allowed ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'}`}>
                      {detailOffer.conditions.change_before_departure.allowed
                        ? `Changeable${detailOffer.conditions.change_before_departure.penalty_amount ? ` (${fmtCurrency(detailOffer.conditions.change_before_departure.penalty_amount, detailOffer.conditions.change_before_departure.penalty_currency)} fee)` : ''}`
                        : 'Non-changeable'}
                    </span>
                  )}
                  {detailOffer.conditions.refund_before_departure && (
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${detailOffer.conditions.refund_before_departure.allowed ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                      {detailOffer.conditions.refund_before_departure.allowed
                        ? `Refundable${detailOffer.conditions.refund_before_departure.penalty_amount ? ` (${fmtCurrency(detailOffer.conditions.refund_before_departure.penalty_amount, detailOffer.conditions.refund_before_departure.penalty_currency)} fee)` : ''}`
                        : 'Non-refundable'}
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Flight segments detail */}
            {(detailOffer.slices?.add || []).map((slice, sIdx) => (
              <div key={sIdx} className="mb-4">
                <h4 className="font-medium text-sm mb-2">
                  {slice.originName || slice.origin} &rarr; {slice.destinationName || slice.destination}
                  <span className="text-gray-400 font-normal ml-2">{fmtDate(slice.departure_date)}</span>
                  {slice.duration && <span className="text-xs text-gray-400 font-normal ml-2">Total: {slice.duration}</span>}
                </h4>
                <div className="space-y-3">
                  {(slice.segments || []).map((seg, segIdx) => (
                    <div key={segIdx} className="border border-gray-200 rounded-lg p-3">
                      {/* Carrier + flight number */}
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center space-x-2">
                          <AirlineLogo code={seg.carrierCode} size="md" />
                          <div>
                            <div className="text-sm font-medium">{seg.carrierName || seg.carrierCode} {seg.carrierCode}{seg.number}</div>
                            {seg.operatingCarrier?.code && seg.operatingCarrier.code !== seg.carrierCode && (
                              <div className="text-xs text-gray-500">
                                Operated by {seg.operatingCarrier.name || seg.operatingCarrier.code} {seg.operatingCarrier.code}{seg.operatingCarrier.flightNumber}
                              </div>
                            )}
                          </div>
                        </div>
                        {seg.cabinClassMarketingName && (
                          <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded font-medium">{seg.cabinClassMarketingName}</span>
                        )}
                      </div>

                      {/* Times and route */}
                      <div className="flex items-center justify-between text-sm mb-2">
                        <div className="text-center">
                          <div className="font-bold">{fmtTime(seg.departure?.at)}</div>
                          <div className="text-xs text-gray-500">{seg.departure?.iataCode}{seg.departure?.terminal ? ` T${seg.departure.terminal}` : ''}</div>
                        </div>
                        <div className="flex-1 mx-3 flex flex-col items-center">
                          <div className="text-xs text-gray-400">{seg.duration}</div>
                          <div className="w-full border-t border-gray-300 mt-1"></div>
                        </div>
                        <div className="text-center">
                          <div className="font-bold">{fmtTime(seg.arrival?.at)}</div>
                          <div className="text-xs text-gray-500">{seg.arrival?.iataCode}{seg.arrival?.terminal ? ` T${seg.arrival.terminal}` : ''}</div>
                        </div>
                      </div>

                      {/* Aircraft, fare basis, baggage */}
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500 border-t border-gray-100 pt-2">
                        {seg.aircraft?.name && (
                          <span>Aircraft: {seg.aircraft.name}{seg.aircraft.code ? ` (${seg.aircraft.code})` : ''}</span>
                        )}
                        {seg.fareBasisCode && (
                          <span>Fare basis: {seg.fareBasisCode}</span>
                        )}
                        {seg.baggages?.length > 0 && seg.baggages.map((bag, bIdx) => (
                          <span key={bIdx} className="capitalize">{bag.type}: {bag.quantity} {bag.quantity === 1 ? 'bag' : 'bags'}</span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}

            {/* Expiry */}
            {detailOffer.expires_at && (
              <p className="text-xs text-gray-400 mb-4">Offer expires: {fmtDate(detailOffer.expires_at)} {fmtTime(detailOffer.expires_at)}</p>
            )}

            {/* Actions */}
            <div className="flex space-x-3">
              <button onClick={() => setStep('offers')} className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50">
                Back
              </button>
              <button onClick={() => handleConfirmChange(detailOffer)}
                className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm font-medium">
                Select &amp; Confirm
              </button>
            </div>
          </div>
        )}

        {/* Confirming */}
        {step === 'confirming' && (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-3 text-gray-600">Confirming flight change...</p>
          </div>
        )}

        {/* Done */}
        {step === 'done' && (
          <div className="text-center py-6">
            <div className="text-green-600 text-4xl mb-3">&#10003;</div>
            <p className="text-lg font-medium">Flight Changed Successfully</p>
            <p className="text-sm text-gray-600 mt-1">Your booking has been updated with the new flight.</p>
            <button onClick={onClose} className="mt-4 px-6 py-2 bg-gray-200 hover:bg-gray-300 rounded-md text-sm font-medium">
              Close
            </button>
          </div>
        )}

        {/* Error */}
        {step === 'error' && (
          <div className="text-center py-6">
            <div className="text-red-500 text-4xl mb-3">&#10007;</div>
            <p className="text-lg font-medium text-red-700">Error</p>
            <p className="text-sm text-gray-600 mt-1">{error}</p>
            <div className="flex space-x-3 mt-4 justify-center">
              <button onClick={() => { setError(''); setStep('select'); }} className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium">
                Try Again
              </button>
              <button onClick={onClose} className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-md text-sm font-medium">
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </ResponsiveModal>
  );
};

// ---- Update Passengers Modal ----
const UpdatePassengersModal = ({ booking, onClose, onUpdated }) => {
  const [step, setStep] = useState('edit'); // edit, saving, done, error
  const [passengers, setPassengers] = useState(
    (booking.passengers || []).map(p => ({
      id: p.id,
      title: p.title || '',
      given_name: p.given_name || '',
      family_name: p.family_name || '',
      email: p.email || '',
      phone_number: p.phone_number || '',
      gender: p.gender || '',
      born_on: p.born_on || '',
    }))
  );
  const [error, setError] = useState('');
  const apiBase = getApiBase();

  const updateField = (idx, field, value) => {
    setPassengers(prev => prev.map((p, i) => i === idx ? { ...p, [field]: value } : p));
  };

  const handleSave = async () => {
    setStep('saving');
    setError('');
    try {
      const paxPayload = passengers.map(p => ({
        id: p.id,
        given_name: p.given_name,
        family_name: p.family_name,
        email: p.email || undefined,
        phone_number: p.phone_number || undefined,
        gender: p.gender || undefined,
        born_on: p.born_on || undefined,
        title: p.title || undefined,
      }));
      const res = await axios.patch(
        `${apiBase}/bookings/flights/${booking.id}/passengers`,
        { passengers: paxPayload },
        { headers: getAuthHeaders() }
      );
      if (res.data.success) {
        setStep('done');
        onUpdated();
      } else {
        setError(res.data.error || 'Update failed');
        setStep('error');
      }
    } catch (e) {
      setError(e.response?.data?.error || e.message);
      setStep('error');
    }
  };

  return (
    <ResponsiveModal onClose={onClose} maxWidth="max-w-2xl" zIndex="z-[80]">
      <div className="p-5 sm:p-6 overflow-y-auto max-h-[85vh] sm:max-h-[80vh]">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Update Passenger Details</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl font-bold">&times;</button>
        </div>

        {step === 'edit' && (
          <div>
            <p className="text-sm text-gray-600 mb-4">Edit passenger information below. Changes will be sent to the airline.</p>
            <div className="space-y-4 mb-4">
              {passengers.map((pax, idx) => (
                <div key={pax.id || idx} className="border border-gray-200 rounded-lg p-4">
                  <div className="text-xs font-bold text-gray-500 uppercase mb-3">Passenger {idx + 1}</div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-gray-600 font-medium">First name</label>
                      <input type="text" value={pax.given_name}
                        onChange={e => updateField(idx, 'given_name', e.target.value)}
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm" />
                    </div>
                    <div>
                      <label className="text-xs text-gray-600 font-medium">Last name</label>
                      <input type="text" value={pax.family_name}
                        onChange={e => updateField(idx, 'family_name', e.target.value)}
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm" />
                    </div>
                    <div>
                      <label className="text-xs text-gray-600 font-medium">Email</label>
                      <input type="email" value={pax.email}
                        onChange={e => updateField(idx, 'email', e.target.value)}
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm" />
                    </div>
                    <div>
                      <label className="text-xs text-gray-600 font-medium">Phone</label>
                      <input type="text" value={pax.phone_number}
                        onChange={e => updateField(idx, 'phone_number', e.target.value)}
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm" />
                    </div>
                    <div>
                      <label className="text-xs text-gray-600 font-medium">Gender</label>
                      <select value={pax.gender}
                        onChange={e => updateField(idx, 'gender', e.target.value)}
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm">
                        <option value="">Select</option>
                        <option value="m">Male</option>
                        <option value="f">Female</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-xs text-gray-600 font-medium">Date of birth</label>
                      <input type="date" value={pax.born_on}
                        onChange={e => updateField(idx, 'born_on', e.target.value)}
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {error && <p className="text-sm text-red-600 mb-3">{error}</p>}
            <div className="flex space-x-3">
              <button onClick={onClose} className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50">Cancel</button>
              <button onClick={handleSave} className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm font-medium">Save Changes</button>
            </div>
          </div>
        )}

        {step === 'saving' && (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-3 text-gray-600">Updating passenger details...</p>
          </div>
        )}

        {step === 'done' && (
          <div className="text-center py-6">
            <div className="text-green-600 text-4xl mb-3">&#10003;</div>
            <p className="text-lg font-medium">Passengers Updated</p>
            <p className="text-sm text-gray-600 mt-1">Passenger details have been updated with the airline.</p>
            <button onClick={onClose} className="mt-4 px-6 py-2 bg-gray-200 hover:bg-gray-300 rounded-md text-sm font-medium">Close</button>
          </div>
        )}

        {step === 'error' && (
          <div className="text-center py-6">
            <div className="text-red-500 text-4xl mb-3">&#10007;</div>
            <p className="text-lg font-medium text-red-700">Error</p>
            <p className="text-sm text-gray-600 mt-1">{error}</p>
            <div className="flex space-x-3 mt-4 justify-center">
              <button onClick={() => { setError(''); setStep('edit'); }} className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium">Try Again</button>
              <button onClick={onClose} className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-md text-sm font-medium">Close</button>
            </div>
          </div>
        )}
      </div>
    </ResponsiveModal>
  );
};

// ---- Booking Detail Modal ----
const BookingDetailModal = ({ bookingId, onClose, onBookingUpdated }) => {
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCancel, setShowCancel] = useState(false);
  const [showChange, setShowChange] = useState(false);
  const [showUpdate, setShowUpdate] = useState(false);
  const [airlineChanges, setAirlineChanges] = useState([]);
  const [loadingAC, setLoadingAC] = useState(false);
  const apiBase = getApiBase();
  

  const fetchBooking = useCallback(async () => {
    try {
      const res = await axios.get(`${apiBase}/bookings/flights/${bookingId}`, { headers: getAuthHeaders() });
      if (res.data.success) {
        setBooking(res.data.data);
        // If there are pending airline changes, load them
        if (res.data.data.has_airline_changes) {
          setLoadingAC(true);
          const acRes = await axios.get(`${apiBase}/bookings/flights/${bookingId}/airline-changes`, { headers: getAuthHeaders() });
          if (acRes.data.success) setAirlineChanges(acRes.data.data || []);
          setLoadingAC(false);
        }
      }
    } catch (e) {
      console.error('Error fetching booking detail:', e);
    } finally {
      setLoading(false);
    }
  }, [bookingId, apiBase]);

  useEffect(() => { fetchBooking(); }, [fetchBooking]);

  const handleAcceptAirlineChange = async (changeId) => {
    try {
      await axios.post(`${apiBase}/bookings/flights/${bookingId}/airline-changes/${changeId}/accept`, {}, { headers: getAuthHeaders() });
      fetchBooking();
      onBookingUpdated();
    } catch (e) {
      console.error('Error accepting airline change:', e);
    }
  };

  if (loading) {
    return (
      <ResponsiveModal onClose={onClose}>
        <div className="p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-3 text-gray-600">Loading booking...</p>
        </div>
      </ResponsiveModal>
    );
  }

  if (!booking) return null;

  const conditions = booking.conditions || {};
  const duffelActions = booking.available_actions || [];
  const hasActions = duffelActions.length > 0;
  const canCancel = booking.booking_status !== 'cancelled' && (
    hasActions
      ? duffelActions.includes('cancel')
      : conditions.refund_before_departure?.allowed !== undefined
  );
  const canChange = booking.booking_status !== 'cancelled' && (
    hasActions
      ? duffelActions.includes('change')
      : !!conditions.change_before_departure?.allowed
  );
  const canUpdate = booking.booking_status !== 'cancelled' && duffelActions.includes('update');
  const segments = booking.segments || [];

  return (
    <ResponsiveModal onClose={onClose}>
      {/* Sticky header */}
      <div className="sticky top-0 bg-white border-b border-gray-200 px-5 sm:px-6 py-4 z-10">
        <div className="flex justify-between items-center">
          <div>
            <h3 className="text-lg sm:text-xl font-semibold">
              {booking.departure_airport} &rarr; {booking.arrival_airport}
              {booking.is_round_trip && ` \u2192 ${booking.departure_airport}`}
            </h3>
            <div className="flex items-center space-x-2 mt-1">
              <span className="text-sm text-gray-500">Ref: {booking.duffel_booking_reference || booking.id?.slice(0, 8)}</span>
              <StatusBadge status={booking.has_airline_changes ? 'airline_change_pending' : booking.booking_status} />
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl font-bold p-1">&times;</button>
        </div>
      </div>

      <div className="p-5 sm:p-6 overflow-y-auto max-h-[75vh] sm:max-h-[70vh]">

        {/* Airline Change Alert */}
        {booking.has_airline_changes && airlineChanges.length > 0 && (
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6">
            <div className="flex items-center mb-2">
              <span className="text-orange-600 text-lg mr-2">&#9888;</span>
              <h4 className="font-medium text-orange-800">Airline Schedule Change</h4>
            </div>
            {airlineChanges.filter(c => !c.action_taken).map((change) => (
              <div key={change.id} className="mt-2">
                <p className="text-sm text-orange-700">The airline has changed this flight's schedule. Please review and take action.</p>
                <div className="flex space-x-2 mt-3">
                  {change.available_actions?.includes('accept') && (
                    <button onClick={() => handleAcceptAirlineChange(change.id)}
                      className="px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-sm rounded-md font-medium">
                      Accept Change
                    </button>
                  )}
                  {change.available_actions?.includes('cancel') && canCancel && (
                    <button onClick={() => setShowCancel(true)}
                      className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white text-sm rounded-md font-medium">
                      Cancel Booking
                    </button>
                  )}
                  {change.available_actions?.includes('change') && (
                    <button onClick={() => setShowChange(true)}
                      className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-md font-medium">
                      Pick Different Flight
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left: Flight info */}
          <div className="lg:col-span-2 space-y-4">
            <h4 className="font-medium text-gray-700">Flight Itinerary</h4>

            {segments.length > 0 ? (
              segments.map((seg, idx) => (
                <div key={idx} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <AirlineLogo code={seg.carrierCode} size="md" />
                      <span className="font-medium">{seg.carrierCode} {seg.number}</span>
                    </div>
                    <span className="text-xs text-gray-400">Segment {idx + 1}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <div className="text-gray-500">Departure</div>
                      <div className="font-medium">{seg.departure_iataCode || seg.departure_airport}</div>
                      <div>{fmtDate(seg.departure_at)} {fmtTime(seg.departure_at)}</div>
                    </div>
                    <div>
                      <div className="text-gray-500">Arrival</div>
                      <div className="font-medium">{seg.arrival_iataCode || seg.arrival_airport}</div>
                      <div>{fmtDate(seg.arrival_at)} {fmtTime(seg.arrival_at)}</div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-2">
                  <AirlineLogo code={booking.airline} size="md" />
                  <span className="font-medium">{booking.airline} {booking.flight_number}</span>
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <div className="text-gray-500">Departure</div>
                    <div className="font-medium">{booking.departure_airport}</div>
                    <div>{fmtDate(booking.departure_date)}</div>
                  </div>
                  <div>
                    <div className="text-gray-500">Arrival</div>
                    <div className="font-medium">{booking.arrival_airport}</div>
                    <div>{fmtDate(booking.arrival_date)}</div>
                  </div>
                </div>
              </div>
            )}

            {/* Passengers */}
            {booking.passengers && booking.passengers.length > 0 && (
              <div>
                <h4 className="font-medium text-gray-700 mb-2">Passengers</h4>
                <div className="space-y-1">
                  {booking.passengers.map((pax, i) => (
                    <div key={i} className="text-sm text-gray-600">
                      {pax.title && <span className="capitalize">{pax.title} </span>}
                      {pax.given_name} {pax.family_name}
                      {pax.email && <span className="text-gray-400 ml-2">{pax.email}</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Conditions */}
            {(conditions.change_before_departure || conditions.refund_before_departure) && (
              <div>
                <h4 className="font-medium text-gray-700 mb-2">Fare Conditions</h4>
                <div className="flex flex-wrap gap-2">
                  {conditions.refund_before_departure && (
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${conditions.refund_before_departure.allowed ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                      {conditions.refund_before_departure.allowed
                        ? `Refundable${conditions.refund_before_departure.penalty_amount ? ` (${fmtCurrency(conditions.refund_before_departure.penalty_amount, conditions.refund_before_departure.penalty_currency)} fee)` : ''}`
                        : 'Non-refundable'}
                    </span>
                  )}
                  {conditions.change_before_departure && (
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${conditions.change_before_departure.allowed ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'}`}>
                      {conditions.change_before_departure.allowed
                        ? `Changeable${conditions.change_before_departure.penalty_amount ? ` (${fmtCurrency(conditions.change_before_departure.penalty_amount, conditions.change_before_departure.penalty_currency)} fee)` : ''}`
                        : 'Non-changeable'}
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Right: Summary & Actions */}
          <div className="space-y-4">
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="font-medium mb-3">Booking Summary</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Total</span>
                  <span className="font-bold">{fmtCurrency(booking.total_amount, booking.currency)}</span>
                </div>
                {booking.pricing?.markup?.total_markup > 0 && (
                  <div className="flex justify-between text-gray-500">
                    <span>Service fee</span>
                    <span>{fmtCurrency(booking.pricing.markup.total_markup, booking.currency)}</span>
                  </div>
                )}
                <div className="flex justify-between text-gray-500">
                  <span>Class</span>
                  <span>{booking.class_type || 'Economy'}</span>
                </div>
                <div className="flex justify-between text-gray-500">
                  <span>Booked</span>
                  <span>{fmtDate(booking.created_at)}</span>
                </div>
              </div>
            </div>

            {booking.booking_status === 'cancelled' && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-center">
                <span className="text-red-700 text-sm font-medium">This booking has been cancelled</span>
                {booking.refund_amount && (
                  <p className="text-xs text-red-600 mt-1">Refund: {fmtCurrency(booking.refund_amount, booking.refund_currency)}</p>
                )}
              </div>
            )}

            {/* Change history */}
            {booking.change_history && booking.change_history.length > 0 && (
              <div>
                <h5 className="text-xs font-semibold text-gray-500 uppercase mb-2">Change History</h5>
                <div className="space-y-1">
                  {booking.change_history.map((ch, i) => (
                    <div key={i} className="text-xs text-gray-500">
                      Changed on {fmtDate(ch.confirmed_at)} — {fmtCurrency(ch.change_total_amount, ch.change_total_currency)}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

      </div>

      {/* Sticky footer with actions on mobile */}
      {booking.booking_status !== 'cancelled' && (canChange || canUpdate || canCancel) && (
        <div className="sticky bottom-0 bg-white border-t border-gray-200 px-5 sm:px-6 py-3 sm:py-4">
          <div className="flex flex-col sm:flex-row sm:justify-end space-y-2 sm:space-y-0 sm:space-x-3">
            {canChange && (
              <button onClick={() => setShowChange(true)}
                className="w-full sm:w-auto px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm font-medium min-h-[44px]">
                Change Flight
              </button>
            )}
            {canUpdate && (
              <button onClick={() => setShowUpdate(true)}
                className="w-full sm:w-auto px-4 py-2.5 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-md text-sm font-medium min-h-[44px]">
                Update Passengers
              </button>
            )}
            {canCancel && (
              <button onClick={() => setShowCancel(true)}
                className="w-full sm:w-auto px-4 py-2.5 border border-red-300 text-red-600 hover:bg-red-50 rounded-md text-sm font-medium min-h-[44px]">
                Cancel Booking
              </button>
            )}
          </div>
        </div>
      )}

      {/* Sub-modals */}
      {showCancel && (
        <CancelFlowModal
          booking={booking}
          onClose={() => setShowCancel(false)}
          onCancelled={() => { fetchBooking(); onBookingUpdated(); }}
        />
      )}
      {showChange && (
        <ChangeFlowModal
          booking={booking}
          onClose={() => setShowChange(false)}
          onChanged={() => { fetchBooking(); onBookingUpdated(); }}
        />
      )}
      {showUpdate && (
        <UpdatePassengersModal
          booking={booking}
          onClose={() => setShowUpdate(false)}
          onUpdated={() => { fetchBooking(); onBookingUpdated(); }}
        />
      )}
    </ResponsiveModal>
  );
};

// ---- Hotel Detail Modal ----
const HotelDetailModal = ({ bookingId, onClose, onBookingUpdated }) => {
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [error, setError] = useState('');
  const apiBase = getApiBase();

  const fetchBooking = useCallback(async () => {
    try {
      const res = await axios.get(`${apiBase}/bookings/hotels/${bookingId}`, { headers: getAuthHeaders() });
      if (res.data.success) {
        setBooking(res.data.data);
      }
    } catch (e) {
      console.error('Error fetching hotel booking:', e);
    } finally {
      setLoading(false);
    }
  }, [bookingId, apiBase]);

  useEffect(() => { fetchBooking(); }, [fetchBooking]);

  const startEditing = () => {
    setEditData({
      check_in_date: booking.check_in_date || '',
      check_out_date: booking.check_out_date || '',
      room_type: booking.room_type || '',
      guests: booking.guests || 1,
      traveler_name: booking.traveler_name || '',
      notes: booking.notes || '',
    });
    setEditing(true);
    setError('');
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const res = await axios.patch(`${apiBase}/bookings/hotels/${bookingId}`, editData, { headers: getAuthHeaders() });
      if (res.data.success) {
        setBooking(res.data.data);
        setEditing(false);
        onBookingUpdated();
      } else {
        setError(res.data.error || 'Update failed');
      }
    } catch (e) {
      setError(e.response?.data?.detail || e.response?.data?.error || e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = async () => {
    setCancelling(true);
    setError('');
    try {
      const res = await axios.post(`${apiBase}/bookings/hotels/${bookingId}/cancel`, {}, { headers: getAuthHeaders() });
      if (res.data.success) {
        setShowCancelConfirm(false);
        fetchBooking();
        onBookingUpdated();
      } else {
        setError(res.data.error || 'Cancellation failed');
      }
    } catch (e) {
      setError(e.response?.data?.detail || e.response?.data?.error || e.message);
    } finally {
      setCancelling(false);
    }
  };

  if (loading) {
    return (
      <ResponsiveModal onClose={onClose}>
        <div className="p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-3 text-gray-600">Loading hotel booking...</p>
        </div>
      </ResponsiveModal>
    );
  }

  if (!booking) return null;

  const nights = calcNights(booking.check_in_date, booking.check_out_date);

  return (
    <ResponsiveModal onClose={onClose}>
      {/* Sticky header */}
      <div className="sticky top-0 bg-white border-b border-gray-200 px-5 sm:px-6 py-4 z-10">
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-3">
            <span className="h-10 w-10 bg-gray-100 rounded-lg flex items-center justify-center text-blue-500 flex-shrink-0">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>
            </span>
            <div>
              <h3 className="text-lg sm:text-xl font-semibold">{booking.hotel_name}</h3>
              <div className="flex items-center space-x-2 mt-0.5">
                <span className="text-sm text-gray-500">{booking.confirmation_number || booking.id?.slice(0, 8)}</span>
                <StatusBadge status={booking.booking_status} />
              </div>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl font-bold p-1">&times;</button>
        </div>
      </div>

      <div className="p-5 sm:p-6 overflow-y-auto max-h-[75vh] sm:max-h-[70vh]">
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* Key facts grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <div className="bg-gray-50 rounded-lg p-3 text-center">
            <div className="text-xs text-gray-500 font-medium uppercase">Check-in</div>
            <div className="font-semibold text-sm mt-1">{fmtDateShort(booking.check_in_date)}</div>
          </div>
          <div className="bg-gray-50 rounded-lg p-3 text-center">
            <div className="text-xs text-gray-500 font-medium uppercase">Check-out</div>
            <div className="font-semibold text-sm mt-1">{fmtDateShort(booking.check_out_date)}</div>
          </div>
          <div className="bg-gray-50 rounded-lg p-3 text-center">
            <div className="text-xs text-gray-500 font-medium uppercase">Nights</div>
            <div className="font-semibold text-sm mt-1">{nights}</div>
          </div>
          <div className="bg-gray-50 rounded-lg p-3 text-center">
            <div className="text-xs text-gray-500 font-medium uppercase">Guests</div>
            <div className="font-semibold text-sm mt-1">{booking.guests || 1}</div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="border border-gray-200 rounded-lg p-4">
            {booking.address && <div className="text-sm text-gray-500 mb-3">{booking.address}</div>}

              {editing ? (
                <div className="space-y-3">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-gray-600 font-medium">Check-in date</label>
                      <input type="date" value={editData.check_in_date}
                        onChange={e => setEditData(d => ({...d, check_in_date: e.target.value}))}
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2.5 text-sm min-h-[44px]" />
                    </div>
                    <div>
                      <label className="text-xs text-gray-600 font-medium">Check-out date</label>
                      <input type="date" value={editData.check_out_date}
                        onChange={e => setEditData(d => ({...d, check_out_date: e.target.value}))}
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2.5 text-sm min-h-[44px]" />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-gray-600 font-medium">Room type</label>
                      <input type="text" value={editData.room_type}
                        onChange={e => setEditData(d => ({...d, room_type: e.target.value}))}
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2.5 text-sm min-h-[44px]" />
                    </div>
                    <div>
                      <label className="text-xs text-gray-600 font-medium">Guests</label>
                      <input type="number" min="1" value={editData.guests}
                        onChange={e => setEditData(d => ({...d, guests: parseInt(e.target.value) || 1}))}
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2.5 text-sm min-h-[44px]" />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-gray-600 font-medium">Traveler name</label>
                    <input type="text" value={editData.traveler_name}
                      onChange={e => setEditData(d => ({...d, traveler_name: e.target.value}))}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2.5 text-sm min-h-[44px]" />
                  </div>
                  <div>
                    <label className="text-xs text-gray-600 font-medium">Notes</label>
                    <textarea value={editData.notes}
                      onChange={e => setEditData(d => ({...d, notes: e.target.value}))}
                      rows={2}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2.5 text-sm" />
                  </div>
                </div>
              ) : (
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Room type</span>
                    <span className="font-medium">{booking.room_type || 'Standard Room'}</span>
                  </div>
                  {booking.traveler_name && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Traveler</span>
                      <span className="font-medium">{booking.traveler_name}</span>
                    </div>
                  )}
                  {booking.notes && (
                    <div>
                      <div className="text-gray-500 mb-1">Notes</div>
                      <div className="text-gray-700">{booking.notes}</div>
                    </div>
                  )}
                </div>
              )}
            </div>

          {/* Booking Summary */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="font-medium mb-3 text-sm">Booking Summary</h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Total</span>
                <span className="font-bold text-base">{fmtCurrency(booking.total_price, booking.currency)}</span>
              </div>
              <div className="flex justify-between text-gray-500">
                <span>Per night</span>
                <span>{nights > 0 ? fmtCurrency(parseFloat(booking.total_price) / nights, booking.currency) : '-'}</span>
              </div>
              <div className="flex justify-between text-gray-500">
                <span>Booked</span>
                <span>{fmtDate(booking.created_at)}</span>
              </div>
            </div>
          </div>

          {booking.booking_status === 'cancelled' && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-center">
              <span className="text-red-700 text-sm font-medium">This booking has been cancelled</span>
              {booking.cancelled_at && (
                <p className="text-xs text-red-600 mt-1">Cancelled on {fmtDate(booking.cancelled_at)}</p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Sticky footer with actions */}
      {booking.booking_status !== 'cancelled' && (
        <div className="sticky bottom-0 bg-white border-t border-gray-200 px-5 sm:px-6 py-3 sm:py-4">
          {editing ? (
            <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3 sm:justify-end">
              <button onClick={() => setEditing(false)} className="w-full sm:w-auto px-4 py-2.5 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 min-h-[44px]">Cancel</button>
              <button onClick={handleSave} disabled={saving}
                className="w-full sm:w-auto px-4 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white rounded-md text-sm font-medium min-h-[44px]">
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          ) : (
            <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3 sm:justify-end">
              <button onClick={startEditing}
                className="w-full sm:w-auto px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm font-medium min-h-[44px]">
                Edit Booking
              </button>
              <button onClick={() => setShowCancelConfirm(true)}
                className="w-full sm:w-auto px-4 py-2.5 border border-red-300 text-red-600 hover:bg-red-50 rounded-md text-sm font-medium min-h-[44px]">
                Cancel Booking
              </button>
            </div>
          )}
        </div>
      )}

      {/* Cancel confirmation sub-modal */}
      {showCancelConfirm && (
        <ResponsiveModal onClose={() => setShowCancelConfirm(false)} maxWidth="max-w-md" zIndex="z-[80]">
          <div className="p-5 sm:p-6">
            <h3 className="text-lg font-semibold mb-4">Cancel Hotel Booking</h3>
            <p className="text-sm text-gray-600 mb-4">
              Are you sure you want to cancel your booking at <strong>{booking.hotel_name}</strong>?
              {booking.duffel_booking_id && ' This will cancel your reservation with the hotel provider.'}
              {' '}This action cannot be undone.
            </p>
            {error && <p className="text-sm text-red-600 mb-3">{error}</p>}
            <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
              <button onClick={() => setShowCancelConfirm(false)}
                className="flex-1 px-4 py-2.5 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 min-h-[44px]">
                Keep Booking
              </button>
              <button onClick={handleCancel} disabled={cancelling}
                className="flex-1 px-4 py-2.5 bg-red-600 hover:bg-red-700 disabled:bg-gray-300 text-white rounded-md text-sm font-medium min-h-[44px]">
                {cancelling ? 'Cancelling...' : 'Confirm Cancellation'}
              </button>
            </div>
          </div>
        </ResponsiveModal>
      )}
    </ResponsiveModal>
  );
};

// ---- Flight Bookings List ----
const FlightBookingsList = ({ statusFilter, onCountUpdate }) => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBookingId, setSelectedBookingId] = useState(null);
  const apiBase = getApiBase();

  const fetchBookings = useCallback(async () => {
    setLoading(true);
    try {
      const params = statusFilter ? `?status=${statusFilter}` : '';
      const res = await axios.get(`${apiBase}/bookings/flights${params}`, { headers: getAuthHeaders() });
      if (res.data.success) {
        const data = res.data.data || [];
        setBookings(data);
        onCountUpdate?.(data.length);
      }
    } catch (e) {
      console.error('Error fetching bookings:', e);
    } finally {
      setLoading(false);
    }
  }, [apiBase, statusFilter, onCountUpdate]);

  useEffect(() => { fetchBookings(); }, [fetchBookings]);

  const { upcoming, past } = groupByTime(bookings, 'departure_date');

  const renderFlightCard = (booking) => {
    const effectiveStatus = booking.has_airline_changes ? 'airline_change_pending' : booking.booking_status;
    const passengerNames = booking.passengers?.length > 0
      ? booking.passengers.map(p => `${p.given_name || ''} ${p.family_name || ''}`.trim()).filter(Boolean).join(', ')
      : null;
    return (
      <BookingCard key={booking.id} status={effectiveStatus} onClick={() => setSelectedBookingId(booking.id)}>
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between">
          <div className="flex items-start space-x-3 sm:space-x-4">
            <AirlineLogo code={booking.airline} size="lg" />
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <span className="font-medium text-gray-900 text-sm sm:text-base">
                  {booking.airline} {booking.flight_number} &nbsp; {booking.departure_airport} &rarr; {booking.arrival_airport}
                  {booking.is_round_trip && ` \u2192 ${booking.departure_airport}`}
                </span>
                <StatusBadge status={effectiveStatus} />
              </div>
              <div className="text-sm text-gray-700 font-medium mt-0.5">
                {fmtDate(booking.departure_date)}
              </div>
              {passengerNames && (
                <div className="flex flex-wrap items-center gap-x-2 text-xs sm:text-sm text-gray-500 mt-1">
                  <span>Passengers: {passengerNames}</span>
                  {booking.duffel_booking_reference && (
                    <span className="text-gray-400 hidden sm:inline">&middot; Ref: {booking.duffel_booking_reference}</span>
                  )}
                </div>
              )}
              {!passengerNames && booking.duffel_booking_reference && (
                <div className="text-xs text-gray-400 mt-1">Ref: {booking.duffel_booking_reference}</div>
              )}
            </div>
          </div>
          <div className="text-right mt-2 sm:mt-0 sm:flex-shrink-0">
            <div className="font-semibold text-blue-600 text-sm sm:text-base">{fmtCurrency(booking.total_amount, booking.currency)}</div>
          </div>
        </div>
        {booking.has_airline_changes && (
          <div className="mt-2 pt-2 border-t border-orange-100">
            <span className="text-xs text-orange-600 font-medium">&#9888; Airline schedule change &mdash; action required</span>
          </div>
        )}
      </BookingCard>
    );
  };

  return (
    <div>
      {loading && (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-3 text-gray-500">Loading bookings...</p>
        </div>
      )}

      {!loading && bookings.length === 0 && (
        <EmptyState
          icon="&#9992;&#65039;"
          title="No flight bookings found"
          subtitle={statusFilter ? 'Try a different status filter' : 'Book a flight to see it here'}
        />
      )}

      {!loading && bookings.length > 0 && (
        <div>
          {upcoming.length > 0 && (
            <>
              <SectionDivider label="Upcoming" count={upcoming.length} />
              <div className="space-y-2 sm:space-y-3">
                {upcoming.map(renderFlightCard)}
              </div>
            </>
          )}
          {past.length > 0 && (
            <>
              <SectionDivider label="Past" count={past.length} />
              <div className="space-y-2 sm:space-y-3">
                {past.map(renderFlightCard)}
              </div>
            </>
          )}
        </div>
      )}

      {selectedBookingId && (
        <BookingDetailModal bookingId={selectedBookingId} onClose={() => setSelectedBookingId(null)} onBookingUpdated={fetchBookings} />
      )}
    </div>
  );
};

// ---- Hotel Bookings List ----
const HotelBookingsList = ({ statusFilter, onCountUpdate }) => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBookingId, setSelectedBookingId] = useState(null);
  const apiBase = getApiBase();

  const fetchBookings = useCallback(async () => {
    setLoading(true);
    try {
      const params = statusFilter ? `?status=${statusFilter}` : '';
      const res = await axios.get(`${apiBase}/bookings/hotels${params}`, { headers: getAuthHeaders() });
      if (res.data.success) {
        const data = res.data.data || [];
        setBookings(data);
        onCountUpdate?.(data.length);
      }
    } catch (e) {
      console.error('Error fetching hotel bookings:', e);
    } finally {
      setLoading(false);
    }
  }, [apiBase, statusFilter, onCountUpdate]);

  useEffect(() => { fetchBookings(); }, [fetchBookings]);

  const { upcoming, past } = groupByTime(bookings, 'check_in_date');

  const renderHotelCard = (booking) => {
    const nights = calcNights(booking.check_in_date, booking.check_out_date);
    return (
      <BookingCard key={booking.id} status={booking.booking_status} onClick={() => setSelectedBookingId(booking.id)}>
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <span className="font-medium text-gray-900 text-sm sm:text-base">{booking.hotel_name}</span>
              <StatusBadge status={booking.booking_status} />
            </div>
            {booking.address && (
              <div className="text-sm text-gray-500 mt-0.5">{booking.address}</div>
            )}
            <div className="flex flex-wrap items-center gap-x-2 text-sm text-gray-700 font-medium mt-1.5">
              <span>{fmtDateShort(booking.check_in_date)} &rarr; {fmtDateShort(booking.check_out_date)}</span>
              <span className="text-gray-400">&middot;</span>
              <span>{nights} night{nights > 1 ? 's' : ''}</span>
              {booking.traveler_name && (
                <>
                  <span className="text-gray-400">&middot;</span>
                  <span className="text-gray-600 font-normal">Guest: {booking.traveler_name}</span>
                </>
              )}
            </div>
            {booking.confirmation_number && (
              <div className="text-xs text-gray-400 mt-1">Ref: <span className="font-mono">{booking.confirmation_number}</span></div>
            )}
          </div>
          <div className="text-right mt-2 sm:mt-0 sm:flex-shrink-0">
            <div className="font-semibold text-blue-600 text-sm sm:text-base">{fmtCurrency(booking.total_price, booking.currency)}</div>
          </div>
        </div>
      </BookingCard>
    );
  };

  return (
    <div>
      {loading && (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-3 text-gray-500">Loading hotel bookings...</p>
        </div>
      )}

      {!loading && bookings.length === 0 && (
        <EmptyState
          icon="&#127976;"
          title="No hotel bookings found"
          subtitle={statusFilter ? 'Try a different status filter' : 'Book a hotel to see it here'}
        />
      )}

      {!loading && bookings.length > 0 && (
        <div>
          {upcoming.length > 0 && (
            <>
              <SectionDivider label="Upcoming" count={upcoming.length} />
              <div className="space-y-2 sm:space-y-3">
                {upcoming.map(renderHotelCard)}
              </div>
            </>
          )}
          {past.length > 0 && (
            <>
              <SectionDivider label="Past" count={past.length} />
              <div className="space-y-2 sm:space-y-3">
                {past.map(renderHotelCard)}
              </div>
            </>
          )}
        </div>
      )}

      {selectedBookingId && (
        <HotelDetailModal bookingId={selectedBookingId} onClose={() => setSelectedBookingId(null)} onBookingUpdated={fetchBookings} />
      )}
    </div>
  );
};

// ---- Main Booking Management Component ----
const BookingManagement = () => {
  const [activeTab, setActiveTab] = useState('flights');
  const [statusFilter, setStatusFilter] = useState('');
  const [flightCount, setFlightCount] = useState(null);
  const [hotelCount, setHotelCount] = useState(null);

  const tabs = [
    { id: 'flights', label: 'Flights', count: flightCount },
    { id: 'hotels', label: 'Hotels', count: hotelCount },
  ];

  return (
    <div className="px-3 sm:px-6 lg:px-8 py-4 sm:py-6 bg-white min-h-screen">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <h2 className="text-xl font-semibold text-gray-900">Bookings</h2>
        <div className="flex items-center space-x-3">
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm min-h-[44px] sm:min-h-0 sm:py-1.5 bg-white">
            <option value="">All Statuses</option>
            <option value="confirmed">Confirmed</option>
            <option value="cancelled">Cancelled</option>
            <option value="changed">Changed</option>
          </select>
        </div>
      </div>

      {/* Mobile: pill tabs; Desktop: underline tabs */}
      {/* Mobile pill tabs */}
      <div className="sm:hidden flex bg-gray-100 rounded-full p-1 mb-5">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 text-center py-2.5 rounded-full text-sm font-medium transition-all min-h-[44px] ${
              activeTab === tab.id
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-gray-500'
            }`}
          >
            {tab.label}
            {tab.count !== null && (
              <span className={`ml-1.5 text-xs px-1.5 py-0.5 rounded-full ${
                activeTab === tab.id ? 'bg-blue-100 text-blue-600' : 'bg-gray-200 text-gray-500'
              }`}>{tab.count}</span>
            )}
          </button>
        ))}
      </div>
      {/* Desktop underline tabs */}
      <div className="hidden sm:block border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-6">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
              {tab.count !== null && (
                <span className={`ml-1.5 text-xs px-1.5 py-0.5 rounded-full ${
                  activeTab === tab.id ? 'bg-blue-100 text-blue-600' : 'bg-gray-200 text-gray-500'
                }`}>{tab.count}</span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {activeTab === 'flights' && <FlightBookingsList statusFilter={statusFilter} onCountUpdate={setFlightCount} />}
      {activeTab === 'hotels' && <HotelBookingsList statusFilter={statusFilter} onCountUpdate={setHotelCount} />}
    </div>
  );
};

export default BookingManagement;
